import { type ReactNode } from 'react'
import { Head, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { BillingProps } from '@/types'

const PRO_FEATURES = [
  'Priority event listing placement',
  'Advanced analytics & insights',
  'Custom branding options',
  'Dedicated support',
]

function titleizeStatus(status: string): string {
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export default function BillingShow({
  subscription_status,
  current_period_end,
  pro,
  has_subscription,
  complimentary,
}: BillingProps) {
  const isPastDue = has_subscription && subscription_status === 'past_due'
  const planTitle = pro ? 'Pro Plan' : has_subscription ? 'Subscription' : 'Free Plan'
  const planDescription = complimentary
    ? 'Your organization has complimentary Pro access.'
    : pro
    ? 'You have full access to all Pro features.'
    : isPastDue
      ? 'Your subscription needs attention. Update your payment method to avoid interruption.'
      : has_subscription && subscription_status
        ? `Current subscription status: ${titleizeStatus(subscription_status)}.`
        : 'Upgrade to Pro to unlock premium features for your organization.'
  const badgeLabel = pro ? 'Pro' : has_subscription && subscription_status ? titleizeStatus(subscription_status) : 'Free'
  const badgeVariant = pro ? 'default' : isPastDue ? 'destructive' : has_subscription ? 'outline' : 'secondary'

  return (
    <>
      <Head title="Billing" />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="mb-8 text-4xl font-semibold tracking-tight">Billing</h1>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{planTitle}</CardTitle>
                <CardDescription>{planDescription}</CardDescription>
              </div>
              <Badge variant={badgeVariant}>{badgeLabel}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {has_subscription && current_period_end && (
              <p className="text-base text-muted-foreground">
                Current period ends {new Date(current_period_end).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            )}

            {isPastDue && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
                <p className="text-base text-destructive">
                  Your payment is past due. Please update your payment method to avoid interruption.
                </p>
              </div>
            )}

            {!has_subscription && (
              <div className="space-y-3">
                <p className="text-base font-medium">Pro includes:</p>
                <ul className="space-y-2">
                  {PRO_FEATURES.map(feature => (
                    <li key={feature} className="flex items-center gap-2 text-base text-muted-foreground">
                      <svg className="h-4 w-4 shrink-0 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {has_subscription && !complimentary ? (
              <Button
                variant="outline"
                onClick={() => router.post('/dashboard/billing/portal')}
              >
                Manage Billing
              </Button>
            ) : has_subscription ? null : (
              <Button
                onClick={() => router.post('/dashboard/billing/checkout')}
              >
                Upgrade to Pro
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}

BillingShow.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
